// src/Studio/presets/textAnimationPresets.ts
// Text-specific animation presets for character/word/line-level animations

import type { EasingType } from "../engine/keyframeEngine";

// ============ TYPES ============

export type TextSplitMode = "character" | "word" | "line";
export type TextAnimationDirection = "forward" | "reverse" | "center";

export interface TextUnitAnimation {
  opacity?: { from: number; to: number };
  translateY?: { from: number; to: number }; // percentage of line height
  translateX?: { from: number; to: number }; // percentage of character width
  scale?: { from: number; to: number };
}

export interface TextAnimationPreset {
  id: string;
  name: string;
  icon: string;
  description: string;
  splitMode: TextSplitMode;
  unitAnimation: TextUnitAnimation;
  /** Stagger ratio: 0.05 = each unit starts 5% after the previous */
  staggerRatio: number;
  easing: EasingType;
  /** Default duration in seconds */
  defaultDuration: number;
}

// ============ PRESETS ============

export const TEXT_ANIMATION_PRESETS: TextAnimationPreset[] = [
  {
    id: "typewriter",
    name: "Typewriter",
    icon: "Aa|",
    description: "Characters appear one at a time like typing",
    splitMode: "character",
    unitAnimation: {
      opacity: { from: 0, to: 1 },
    },
    staggerRatio: 0.03,
    easing: "linear",
    defaultDuration: 1.5,
  },
  {
    id: "word-by-word",
    name: "Word by Word",
    icon: "Hi_",
    description: "Words fade and slide in sequentially",
    splitMode: "word",
    unitAnimation: {
      opacity: { from: 0, to: 1 },
      translateY: { from: 20, to: 0 },
    },
    staggerRatio: 0.15,
    easing: "easeOut",
    defaultDuration: 1.2,
  },
  {
    id: "line-by-line",
    name: "Line by Line",
    icon: "L1\nL2",
    description: "Each line animates separately",
    splitMode: "line",
    unitAnimation: {
      opacity: { from: 0, to: 1 },
      translateY: { from: 30, to: 0 },
    },
    staggerRatio: 0.25,
    easing: "easeOut",
    defaultDuration: 1.0,
  },
  {
    id: "fade-letters",
    name: "Fade Letters",
    icon: "A.b.c",
    description: "Letters fade in with subtle scale",
    splitMode: "character",
    unitAnimation: {
      opacity: { from: 0, to: 1 },
      scale: { from: 0.8, to: 1 },
    },
    staggerRatio: 0.05,
    easing: "easeOut",
    defaultDuration: 1.0,
  },
];

// ============ HELPERS ============

export function getTextAnimationPreset(id: string): TextAnimationPreset | undefined {
  return TEXT_ANIMATION_PRESETS.find((p) => p.id === id);
}

export function getDefaultTextAnimationParams() {
  return {
    duration: 1.0,
    stagger: 0.05,
    direction: "forward" as TextAnimationDirection,
  };
}

/**
 * Split text into units based on mode
 */
export function splitTextByMode(text: string, mode: TextSplitMode): string[] {
  if (!text) return [];

  switch (mode) {
    case "character":
      return text.split("");
    case "word":
      return text.split(/\s+/).filter(Boolean);
    case "line":
      return text.split(/\n/).filter(Boolean);
    default:
      return [text];
  }
}

/**
 * Get indices for animation direction
 * - forward: 0, 1, 2, 3...
 * - reverse: n-1, n-2, n-3...
 * - center: middle first, then outward
 */
export function getAnimationOrder(
  count: number,
  direction: TextAnimationDirection
): number[] {
  const indices = Array.from({ length: count }, (_, i) => i);

  switch (direction) {
    case "reverse":
      return indices.reverse();
    case "center": {
      const result: number[] = [];
      const mid = Math.floor(count / 2);
      let left = mid;
      let right = mid + 1;

      // Start from center, alternate left/right
      if (count % 2 === 1) {
        result.push(mid);
        left = mid - 1;
      }

      while (left >= 0 || right < count) {
        if (left >= 0) result.push(left--);
        if (right < count) result.push(right++);
      }
      return result;
    }
    case "forward":
    default:
      return indices;
  }
}
